import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { isClinicUserSelector } from 'store/modules/user';
import { useShallowAppSelector } from 'store/hooks';
import CustomText from 'components/common/text/CustomText';
import { T } from 'components/common/text';
import { UserActions, UtilActions } from 'store/actionCreators';
import { IUsertype } from 'lib/types/utilTypes';
import { useDidUpdateEffect } from 'lib/utils';
import { concat } from 'lodash';
import {
  icon_notice,
  picto_type_clinic,
  picto_type_designer,
  picto_type_lab,
  picto_type_milling,
} from 'components/base/images';
import { companyType } from 'lib/mapper';
import cx from 'classnames';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import MuiButton from 'components/common/button/MuiButton';
import { color } from 'styles/utils';

interface AccountTypeChangeModalContainerProps {
  onCloseModal: () => void;
}

function AccountTypeChangeModalContainer({ onCloseModal }: AccountTypeChangeModalContainerProps) {
  const {
    userTypeIdx,
    userTypeListData,
    userTypeChangeableData,
    isClinicUser,
    updateUserTypeSuccess,
  }: {
    userTypeIdx?: number | null;
    userTypeListData: object | any;
    userTypeChangeableData: object | any;
    isClinicUser: boolean;
    updateUserTypeSuccess: boolean;
  } = useShallowAppSelector(state => ({
    userTypeIdx: state.user.user?.userTypeIdx,
    userTypeListData: state.util.usertypeList.data,
    userTypeChangeableData: state.user.fetchUsertypeChangeable.data,
    isClinicUser: isClinicUserSelector(state),
    updateUserTypeSuccess: state.user.updateUserType.success,
  }));

  type ObjType = {
    [index: string]: string;
    clinic: string;
    lab: string;
    milling: string;
    designer: string;
  };

  const typeIconArray: ObjType = {
    clinic: picto_type_clinic,
    lab: picto_type_lab,
    milling: picto_type_milling,
    designer: picto_type_designer,
  };

  const [userTypeList, setUserTypeList] = useState<IUsertype[]>([]);
  const [currentUserTypeIdx, setCurrentUserTypeIdx] = useState<number | null>(null);
  const [isChangeable, setIsChangeable] = useState(true);

  useEffect(() => {
    UtilActions.fetch_user_type_list_request();
    UserActions.fetch_user_type_changeable_request();
  }, []);

  useEffect(() => {
    if (!!userTypeIdx) {
      setCurrentUserTypeIdx(userTypeIdx);
    }
  }, [userTypeIdx]);

  useDidUpdateEffect(() => {
    if (!!userTypeListData) {
      const newUserTypeList: IUsertype[] = userTypeListData?.list.map(
        (userType: Omit<IUsertype, 'typeIcon'>) => {
          return {
            ...userType,
            typeIcon: typeIconArray[userType.typeName.toLowerCase()],
          };
        },
      );

      setUserTypeList(newUserTypeList);
    }
  }, [userTypeListData]);

  useDidUpdateEffect(() => {
    if (!!userTypeChangeableData) {
      if (isClinicUser) {
        const { patientCount, projectCount } = userTypeChangeableData;

        setIsChangeable(patientCount === 0 && projectCount === 0);
      } else {
        const { projectCount } = userTypeChangeableData;

        setIsChangeable(projectCount === 0);
      }
    }
  }, [userTypeChangeableData]);

  useDidUpdateEffect(() => {
    if (updateUserTypeSuccess) {
      onCloseModal();
      UserActions.fetch_profile_request();
    }
  }, [updateUserTypeSuccess]);

  const handleTypeClick = (userTypeIdx: number) => {
    setCurrentUserTypeIdx(userTypeIdx);
  };

  const handleSaveChangedType = () => {
    UserActions.update_user_type_request({ userTypeIdx: currentUserTypeIdx });
  };

  return (
    <StyledAccountTypeChangeModal data-component-name="AccountTypeChangeModal">
      <CustomText fontSize={24} fontWeight={600} fontColor="#222">
        <T>ACCOUNT_TYPE_CHANGE_INFO</T>
      </CustomText>
      <div className="accountTypeChangeModalContainer__type_wrapper">
        <div className="accountTypeChangeModalContainer__type_container">
          {userTypeList.map((userType: IUsertype) => {
            return (
              <button
                className="accountTypeChangeModalContainer__type_item"
                key={userType.userTypeIdx}
                onClick={() => handleTypeClick(userType.userTypeIdx)}
              >
                <div
                  className={cx(`type_item_container`, {
                    selected: userType.userTypeIdx === currentUserTypeIdx,
                  })}
                />
                <div className="type_item_box">
                  <div
                    className={cx(`type_item_image_box`, {
                      selected: userType.userTypeIdx === currentUserTypeIdx,
                    })}
                  >
                    <img
                      src={userType.typeIcon}
                      alt={`picto_type_${userType.typeName.toLowerCase()}`}
                    />
                  </div>
                  <div className="type_item_text_box">
                    <CustomText fontSize={16} fontWeight={500} fontColor="#00A4E3">
                      {userType.typeName}
                    </CustomText>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {!isChangeable && (
          <div className="accountTypeChangeModalContainer__alert_box">
            <img src={icon_notice} alt="icon_notice" />
            <CustomText fontSize={14} fontColor={color.navy_blue}>
              <T>
                {isClinicUser
                  ? 'USER_TYPE_CHANGEABLE_ALERT_CLINIC'
                  : 'USER_TYPE_CHANGEABLE_ALERT_LAB'}
              </T>
            </CustomText>
          </div>
        )}
      </div>
      <StyledPlainButtonOuter backgroundColor={color.navy_blue} left="50%" width={370} height={76}>
        <MuiButton
          type="submit"
          config={{
            width: '320px',
          }}
          disabled={!isChangeable}
          disableElevation
          color="primary"
          variant="contained"
          disablebackground="#17288a"
          disablefontcolor="#ffffff"
          className="xl border-radius-round productModal__submit_btn"
          onClick={handleSaveChangedType}
        >
          <T>GLOBAL_SAVE</T>
        </MuiButton>
      </StyledPlainButtonOuter>
    </StyledAccountTypeChangeModal>
  );
}
const StyledAccountTypeChangeModal = styled.div<{}>`
  width: 100%;
  height: 640px;
  border-radius: 15px;
  padding: 30px;
  background-color: #ffffff;
  position: relative;
  display: flex;
  row-gap: 48px;
  flex-direction: column;
  .accountTypeChangeModalContainer__type_wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    .accountTypeChangeModalContainer__type_container {
      display: flex;
      column-gap: 20px;
      .accountTypeChangeModalContainer__type_item {
        padding: 0;
        width: 100%;
        height: 280px;
        display: flex;
        flex-direction: column;
        position: relative;
        .type_item_container {
          position: absolute;
          display: flex;
          width: 100%;
          height: 100%;
          justify-content: center;
          align-items: center;
          border: 1px solid #e1e1e7;
          border-radius: 10px;
          background-color: #edf4fb;
          &.selected {
            border-color: #00a4e3;
          }
        }
        .type_item_box {
          position: absolute;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          .type_item_image_box {
            display: flex;
            flex-shrink: 0;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 230px;
            border: 1px solid #e1e1e7;
            border-radius: 10px;
            background-color: white;
            &.selected {
              border-color: #00a4e3;
            }
          }
          .type_item_text_box {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          }
        }
      }
    }
    .accountTypeChangeModalContainer__alert_box {
      display: flex;
      column-gap: 5px;
      align-items: center;
    }
  }
`;
export default React.memo(AccountTypeChangeModalContainer);
